import { Restore } from '@mui/icons-material';
import {
    React,
    useState,
    useEffect,
    useMemo,
    useCallback,
    useParams,
    Box,
    Typography,
    GridColDef,
    Dayjs,
    dayjs,
    Grid2,
    IconButton,
    CustomDataGrid,
} from '../../../utils/globalImports';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ListAltIcon from '@mui/icons-material/ListAlt';
import HistoryIcon from '@mui/icons-material/History';

import { useApi } from '../../../utils/globalImports';



const DEFAULT_COLOR = '#1e66ab';


interface Log {
    id: number;
    changesBy: string;
    logDate: Dayjs | null;
    text: string;
}


const getInitials = (name: string): string => {
    if (!name) return '??';
    const parts = name.trim().split(' ');
    if (parts.length > 1) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
};

// Action Column
const ActionCellRenderer: React.FC<{ text: string }> = ({ text }) => {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    if (!text) return null;

    const lines = text.split('\n').filter((line) => line.trim() !== '');
    if (lines.length === 0) return null;

    const renderLineContent = (lineText: string, preservePrefix: boolean = false) => {
        const trimmed = lineText.trim();


        const prefixMatch = trimmed.match(/^([•]\s*)/);
        const prefix = prefixMatch ? prefixMatch[1] : '';
        const withoutPrefix = prefix ? trimmed.substring(prefix.length) : trimmed;

        const colonIndex = withoutPrefix.indexOf(':');

        if (colonIndex !== -1) {
            const heading = withoutPrefix.substring(0, colonIndex).trim();
            const value = withoutPrefix.substring(colonIndex + 1);
            return (
                <Typography
                    sx={{ fontSize: '14px', fontFamily: 'inherit', color: DEFAULT_COLOR }}
                >
                    {preservePrefix && prefix && (
                        <Box component="span" sx={{}}>{prefix}</Box>
                    )}
                    <Box component="span" sx={{ fontWeight: 500 }}>
                        {heading}
                    </Box>
                    :{value}
                </Typography>
            );
        }

        return (
            <Typography
                sx={{ fontSize: '14px', fontFamily: 'inherit', color: DEFAULT_COLOR }}
            >
                {preservePrefix && prefix ? prefix : ''}{withoutPrefix}
            </Typography>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'space-between',
                gap: 2,
                py: 1,
            }}
        >
            <Box
                sx={{
                    flexGrow: 1,
                    bgcolor: '#f5f8ff',
                    borderRadius: '6px',
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid #e8e9f5',
                    overflow: 'hidden',
                    width: '100%',
                }}
            >
                {isExpanded ? (
                    <Box
                        component="ul"
                        sx={{ m: 0, p: 0, listStyleType: 'none', width: '100%' }}
                    >
                        {lines.map((line, idx) => (
                            <Box
                                component="li"
                                key={idx}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',       
                                    mb: idx !== lines.length - 1 ? 1 : 0,
                                }}
                            >
                                <Box                          
                                    sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: '50%',
                                        bgcolor: DEFAULT_COLOR,
                                        mr: 1.5,
                                        flexShrink: 0,
                                    }}
                                />
                                {renderLineContent(line, false)}  
                            </Box>
                        ))}
                    </Box>
                ) : (

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '13px',
                            overflow: 'hidden',
                            width: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                width: 7,
                                height: 7,
                                borderRadius: '50%',
                                bgcolor: DEFAULT_COLOR,
                                mr: 1.5,
                                flexShrink: 0,
                            }}
                        />
                        <Box
                            sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                width: '100%',
                            }}
                        >
                            {renderLineContent(lines[0], false)}
                        </Box>
                    </Box>
                )}
            </Box>

            <IconButton
                onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded((prev) => !prev);
                }}
                size="small"
                sx={{ color: '#62646c', flexShrink: 0 }}
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
                {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
        </Box>
    );
};


const ProposalFeeStageInstructionLog: React.FC = () => {
    const { execute, loading } = useApi();
    const [rows, setRows] = useState<any[]>([]);
    const { feeStageId } = useParams<{ feeStageId?: string }>(); // Get feeStageId from URL

    useEffect(() => {
        if (!feeStageId) return;
        fetchLog();
    }, [feeStageId]);

    const fetchLog = async () => {
        if (!feeStageId) return;
        const { data } = await execute<Log[]>('get', `AuditLogs/${feeStageId}`);
        if (data) {
            setRows(data);
        }
    };


    const columns: GridColDef<(typeof rows)[number]>[] = useMemo(
        () => [
            {
                field: 'changesBy',
                headerName: 'User',
                flex: 1,
                headerAlign: 'left',
                align: 'left',
                renderHeader: () => (
                    <Box
                        sx={{
                            display: 'flex',
                            fontSize: '16px',
                            fontWeight: 450,
                            alignItems: 'center',
                            gap: 1,
                            color: DEFAULT_COLOR,
                        }}
                    >
                        <PersonOutlineIcon sx={{ fontSize: 18 }} />
                        User
                    </Box>
                ),
                renderCell: (params) => (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            width: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                bgcolor: '#eef0fc',
                                color: DEFAULT_COLOR,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 600,
                                fontSize: '13px',
                                flexShrink: 0,
                            }}
                        >
                            {getInitials(params.value || '')}
                        </Box>
                        <Box sx={{ overflow: 'hidden' }}>
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    color: DEFAULT_COLOR,
                                    lineHeight: 1.2,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    fontWeight: 400,
                                }}
                            >
                                {params.value}
                            </Typography>
                        </Box>
                    </Box>
                ),
            },
            {
                field: 'logDate',
                headerName: 'Date and Time',
                flex: 1,
                headerAlign: 'left',
                align: 'left',
                renderHeader: () => (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '16px',
                            fontWeight: 450,
                            gap: 1,
                            color: DEFAULT_COLOR,
                        }}
                    >
                        <CalendarTodayIcon sx={{ fontSize: 16 }} />
                        Date and Time
                    </Box>
                ),
                renderCell: (params) => (
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
                    >
                        <HistoryIcon
                            sx={{ fontSize: 18, color: DEFAULT_COLOR, flexShrink: 0 }}
                        />
                        <Typography
                            sx={{
                                fontSize: '14px',
                                color: DEFAULT_COLOR,
                                whiteSpace: 'nowrap',
                                fontWeight: 400,
                            }}
                        >
                            {params.value
                                ? dayjs(params.value).format('DD-MM-YYYY HH:mm:ss')
                                : 'N/A'}
                        </Typography>
                    </Box>
                ),
            },
            {
                field: 'text',
                headerName: 'Action',
                flex: 2,
                headerAlign: 'left',
                align: 'left',
                renderHeader: () => (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '16px',
                            fontWeight: 450,
                            gap: 1,
                            color: DEFAULT_COLOR,
                        }}
                    >
                        <ListAltIcon sx={{ fontSize: 18 }} />
                        Action
                    </Box>
                ),
                renderCell: (params) => (
                    <ActionCellRenderer text={params.value || ''} />
                ),
            },
        ],
        []
    );

    return (
        <Box
            sx={{ backgroundColor: '#fff', width: '100%', boxSizing: 'border-box' }}
        >
            <Grid2 sx={{ pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                        sx={{
                            backgroundColor: '#f0f4ff',
                            borderRadius: '20px',
                            height: 48,
                            width: 48,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                        }}
                    >
                        <Restore sx={{ height: 30, width: 30, color: DEFAULT_COLOR }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                            component="h1"
                            sx={{
                                color: '#000',
                                fontWeight: 600,
                                fontSize: '1.5rem',
                                mb: 0.5,
                            }}
                        >
                            Activity Log
                        </Typography>
                        <Typography sx={{ color: '#7a7699', fontSize: '14px' }}>
                            Track all recent action updates
                        </Typography>
                    </Box>
                </Box>
            </Grid2>

            <CustomDataGrid
                rows={rows}
                columns={columns}
                setRowHeigt={true}
                customMessage="No fee stage log records are available"
            />
        </Box>
    );
};

export default ProposalFeeStageInstructionLog;
